import React, { useEffect, useState, useMemo } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import MenuBrowser from './components/MenuBrowser';
import ExternalPage from './components/ExternalPage';
import Movies from './components/movies';
import ArchiveAudioPage from './components/ArchiveAudioPage';
import { fetchMenuData } from './lib/menuData';
import AdminApp from './admin/AdminApp';
import { IS_IN_IFRAME } from './utils/iframeDetector';
import { IL_HIDDEN_CATEGORY_IDS } from './lib/constants';

const IS_DEV = process.env.NODE_ENV === 'development';

// ── HMAC-signed token verification ────────────────────────────────────────────
// Token format: <base64url(timestamp_ms)>.<base64url(HMAC-SHA256(secret, timestamp_part))>
// Valid for TOKEN_TTL_MS after issuance. The secret lives only in env vars —
// it is never visible in the URL.
const TOKEN_TTL_MS = 5 * 60 * 1000; // 5 minutes

async function verifyToken(token, secret) {
  try {
    const [tsPart, sigPart] = token.split('.');
    if (!tsPart || !sigPart) return false;

    // Decode timestamp and check age
    const timestamp = parseInt(atob(tsPart.replace(/-/g, '+').replace(/_/g, '/')), 10);
    if (isNaN(timestamp) || Date.now() - timestamp > TOKEN_TTL_MS) return false;

    // Import the secret key
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(secret),
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['verify']
    );

    // Decode the provided signature
    const sigBytes = Uint8Array.from(
      atob(sigPart.replace(/-/g, '+').replace(/_/g, '/')),
      c => c.charCodeAt(0)
    );

    // Verify: HMAC was computed over the timestamp part exactly
    return await crypto.subtle.verify(
      'HMAC', cryptoKey, sigBytes,
      new TextEncoder().encode(tsPart)
    );
  } catch {
    return false;
  }
}

// Determine initial auth status synchronously where possible so there's no flash.
// /admin paths bypass this gate entirely — AdminApp enforces its own check.
function getInitialAuthStatus() {
  if (window.location.pathname.startsWith('/admin')) return 'allowed';
  if (IS_IN_IFRAME || IS_DEV) return 'allowed';
  if (sessionStorage.getItem('bypass_auth') === 'true') return 'allowed';
  const params = new URLSearchParams(window.location.search);
  if (params.get('token')) return 'checking'; // async HMAC verify needed
  return 'blocked';
}

function RestrictedScreen() {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: '100vh',
      background: '#f5f5f7', fontFamily: 'Open Sans, sans-serif',
      color: '#555', textAlign: 'center', padding: '2rem',
    }}>
      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔒</div>
      <h2 style={{ margin: '0 0 0.5rem', color: '#1a1a2e', fontWeight: 700 }}>
        Access Restricted
      </h2>
      <p style={{ maxWidth: 380, lineHeight: 1.6, margin: 0 }}>
        This content is only available through the ICON platform.
        Please contact your administrator if you believe this is an error.
      </p>
    </div>
  );
}

function App() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const ilParam        = queryParams.get("IL");
  const otterbeinParam = queryParams.get("otterbein");
  const typeParam      = queryParams.get("type");

  // ── Auth state ────────────────────────────────────────────
  // 'allowed' | 'blocked' | 'checking'
  const [authStatus, setAuthStatus] = useState(getInitialAuthStatus);

  // Async HMAC verification — only runs when a ?token= param is present
  useEffect(() => {
    if (authStatus !== 'checking') return;
    const secret = process.env.REACT_APP_BYPASS_KEY;
    const token  = queryParams.get('token');
    if (!secret || !token) { setAuthStatus('blocked'); return; }

    verifyToken(token, secret).then(valid => {
      if (valid) {
        sessionStorage.setItem('bypass_auth', 'true');
        // Strip token from address bar so it can't be bookmarked or shared
        const clean = new URL(window.location.href);
        clean.searchParams.delete('token');
        window.history.replaceState({}, '', clean.toString());
        setAuthStatus('allowed');
      } else {
        setAuthStatus('blocked');
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount only

  // ── Menu data from Supabase ───────────────────────────────
  const [rawMenuData, setRawMenuData] = useState(null);
  const [menuError, setMenuError]     = useState(null);

  useEffect(() => {
    fetchMenuData()
      .then(setRawMenuData)
      .catch(err => {
        console.error('Failed to load menu data:', err);
        setMenuError(err);
      });
  }, []);

  const filteredData = useMemo(() => {
    if (!rawMenuData) return null;
    const ilActive = ilParam === 'true' || sessionStorage.getItem('ILFilter') === 'true';
    if (ilActive) {
      return {
        ...rawMenuData,
        categories: rawMenuData.categories.filter(cat => !IL_HIDDEN_CATEGORY_IDS.includes(cat.id)),
      };
    }
    return rawMenuData;
  }, [rawMenuData, ilParam]);

  // ── Facility / session flags ──────────────────────────────
  useEffect(() => {
    if (ilParam === 'true') sessionStorage.setItem('ILFilter', 'true');
    if (otterbeinParam) {
      sessionStorage.setItem('facilityType', 'otterbein');
      sessionStorage.setItem('initialQueryParamKey', 'otterbein');
      sessionStorage.setItem('initialQueryParamValue', otterbeinParam);
    } else if (typeParam === 'otterbein' || typeParam === 'MC') {
      sessionStorage.setItem('facilityType', typeParam);
    }
  }, [ilParam, otterbeinParam, typeParam]);

  // ── Render gates ──────────────────────────────────────────
  // Admin route bypasses the public gate — it runs its own auth inside AdminApp.
  const isAdminPath = location.pathname.startsWith('/admin');

  if (!isAdminPath) {
    if (authStatus === 'checking') return null; // brief — only while HMAC runs (~1ms)
    if (authStatus === 'blocked')  return <RestrictedScreen />;
    if (menuError)  return <div style={{ padding: '2rem', textAlign: 'center', color: '#c00' }}>Unable to load content. Please refresh the page.</div>;
    if (!filteredData) return <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Loading…</div>;
  }

  return (
    <div className="App">
      <Routes>
        {/* Admin surface — has its own token gate inside AdminApp */}
        <Route path="/admin/*" element={<AdminApp />} />

        {/* Public surface */}
        <Route path="/" element={<MenuBrowser data={filteredData} />} />
        <Route path="/category/:categoryId" element={<MenuBrowser data={filteredData} />} />
        <Route path="/category/:categoryId/subcategory/:subcategoryId" element={<MenuBrowser data={filteredData} />} />
        <Route path="/category/:categoryId/subcategory/:subcategoryId/subsubcategory/:subsubcategoryId" element={<ExternalPage data={filteredData} />} />
        <Route path="/external/:categoryId" element={<ExternalPage data={filteredData} />} />
        <Route path="/external/:categoryId/subcategory/:subcategoryId" element={<ExternalPage data={filteredData} />} />
        <Route path="/external/:categoryId/subcategory/:subcategoryId/subsubcategory/:subsubcategoryId" element={<ExternalPage data={filteredData} />} />
        <Route path="/movies/:categoryId" element={<Movies />} />
        <Route path="/movies/:categoryId/subcategory/:subcategoryId" element={<Movies />} />
        <Route path="/archive-video/:categoryId/subcategory/:subcategoryId/subsubcategory/:subsubcategoryId" element={<ExternalPage data={filteredData} kind="archive-video" />} />
        <Route path="/archive-video/:categoryId/subcategory/:subcategoryId" element={<ExternalPage data={filteredData} kind="archive-video" />} />
        <Route path="/archive-audio/:categoryId/subcategory/:subcategoryId/subsubcategory/:subsubcategoryId" element={<ArchiveAudioPage data={filteredData} />} />
        <Route path="/archive-audio/:categoryId/subcategory/:subcategoryId" element={<ArchiveAudioPage data={filteredData} />} />
      </Routes>
    </div>
  );
}

export default App;
